import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Users, Lock, Unlock, Mail, Loader2, RotateCcw } from "lucide-react";

export default function TeamsReleaseCard() {
  const confirm = useConfirm();
  const [released, setReleased] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [batch, setBatch] = useState(null);
  const [queueMeta, setQueueMeta] = useState(null);

  useEffect(() => {
    api.get("/programme").then(({ data }) => {
      setReleased(!!data?.teams_released);
      if (data?.teams_release_batch_id) refreshBatch(data.teams_release_batch_id);
    }).catch(() => setReleased(false));
  }, []);

  const refreshBatch = (id) => api.get(`/admin/email-batch/${id}`).then(({ data }) => setBatch(data)).catch(() => {});

  useEffect(() => {
    if (!batch || batch.done) return;
    const t = setInterval(() => refreshBatch(batch.batch_id), 3000);
    return () => clearInterval(t);
  }, [batch?.batch_id, batch?.done]);

  const startBatch = (data) => {
    setQueueMeta(data);
    if (data.batch_id) refreshBatch(data.batch_id);
  };

  const toggle = async () => {
    const next = !released;
    if (!(await confirm({
      title: next ? "Release teams to all students?" : "Hide teams from students again?",
      description: next
        ? `Every student will immediately see their team, teammates and team workspace.${sendEmail ? " Everyone on the roster with a team is emailed — registered students get 'meet your team', those who haven't signed up yet get 'create your login'." : ""} Mentors and sponsors stay hidden until the Session 2 reveal.`
        : "Students will see 'Your team will be announced at Session 1' until you release again. No data is changed.",
      confirmText: next ? (sendEmail ? "Release & email students" : "Release teams") : "Hide teams",
    }))) return;
    setBusy(true);
    try {
      if (next) {
        const { data } = await api.post("/admin/teams/release", { send_email: sendEmail });
        if (sendEmail) startBatch(data);
        toast.success(sendEmail ? `Teams released · ${data.queued} emails queued — sending now` : "Teams released — students can see their team now");
      } else {
        await api.put("/programme", { teams_released: false });
        toast.success("Teams hidden from students");
      }
      setReleased(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (!(await confirm({
      title: "Re-send the team announcement?",
      description: "Emails everyone on the roster with a team again — registered students get 'meet your team', those who haven't registered get 'create your login'.",
      confirmText: "Re-send emails",
    }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/teams/release-email");
      startBatch(data);
      toast.success(`${data.queued} emails queued — sending now`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  const retryFailed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email-batch/${batch.batch_id}/retry-failed`);
      toast.success(`${data.requeued} re-queued`);
      refreshBatch(batch.batch_id);
    } catch { toast.error("Could not retry"); }
    finally { setBusy(false); }
  };

  if (released === null) return null;
  const inFlight = batch && !batch.done;
  return (
    <div className={`border-2 p-5 ${released ? "border-[#25AAAA] bg-[#E6F7F7]" : "border-[#f4b847] bg-[#FEF9E7]"}`} data-testid="teams-release-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className={`text-[10px] font-semibold tracking-[0.24em] uppercase ${released ? "text-[#0F5A5A]" : "text-[#7A5B17]"}`}>Session 1 · Team release</div>
          <div className="font-display text-2xl font-black tracking-tighter mt-1 text-[#0F1F2E] flex items-center gap-2">
            {released ? <><Unlock size={20} className="text-[#25AAAA]" /> Teams are visible to students</> : <><Lock size={20} className="text-[#7A5B17]" /> Teams hidden from students</>}
          </div>
          <p className="text-xs text-[#525252] mt-2 max-w-2xl">
            {released
              ? "Students can see their team, teammates and workspace. Mentor and sponsor pairings are still controlled by the Session 2 reveal."
              : "You can allocate students to teams on the Roster freely — students see \"Your team will be announced at Session 1\" everywhere until you press Release."}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button onClick={toggle} disabled={busy} data-testid="teams-release-btn"
            className={released ? "bg-[#0A0A0A] hover:bg-[#333] rounded-none" : "bg-[#25AAAA] hover:bg-[#1e7d7d] rounded-none"}>
            <Users size={14} className="mr-2" /> {busy ? "Working…" : released ? "Hide teams again" : "Release teams to students"}
          </Button>
          {!released && (
            <label className="text-[11px] text-[#525252] inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="teams-release-email-toggle" />
              Email every student their team
            </label>
          )}
          {released && (
            <button onClick={resend} disabled={busy || inFlight} className="text-[11px] uppercase tracking-wider text-[#0F1F2E] hover:underline inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="teams-release-resend-btn">
              <Mail size={12} /> Re-send announcement email
            </button>
          )}
        </div>
      </div>

      {batch && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-3 text-xs text-[#525252] space-y-2" data-testid="teams-release-result">
          <div className="flex items-center gap-3 flex-wrap">
            {inFlight && <Loader2 size={14} className="animate-spin text-[#25AAAA]" />}
            <span data-testid="teams-release-progress">
              <strong className="text-[#0F1F2E]">{batch.sent}</strong> of <strong className="text-[#0F1F2E]">{batch.total}</strong> emails sent
              {inFlight && <> · {batch.queued + batch.sending} waiting — paced so the mail service's rate limit never drops anyone; this can take a few minutes</>}
              {batch.failed > 0 && <> · <span className="text-[#A23232] font-semibold">{batch.failed} failed</span></>}
            </span>
            {queueMeta && <span className="text-[#525252]">· {queueMeta.registered} registered · {queueMeta.invited} invited to sign up · {queueMeta.roster_with_team} on roster with a team</span>}
            {batch.done && batch.failed > 0 && (
              <button onClick={retryFailed} disabled={busy} className="inline-flex items-center gap-1 text-[#0F1F2E] hover:underline uppercase tracking-wider" data-testid="teams-release-retry-failed">
                <RotateCcw size={11} /> Retry failed
              </button>
            )}
          </div>
          <div className="h-1.5 bg-white border border-[#E5E7EB]"><div className="h-full bg-[#25AAAA]" style={{ width: `${batch.total ? Math.round((batch.sent / batch.total) * 100) : 0}%` }} /></div>
          {batch.failed_list?.length > 0 && (
            <details data-testid="teams-release-failed">
              <summary className="text-[#A23232] cursor-pointer">Show why they failed</summary>
              <ul className="mt-1 list-disc pl-5">{batch.failed_list.map((f) => <li key={f.email}>{f.name ? `${f.name} · ` : ""}{f.email} — {f.reason}</li>)}</ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
